import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';

import { BULK_UPLOAD_ROWS } from 'queries/bulkOperations';
import { formatDateTime } from 'utilities/datesAndTimes';
import {
  DOWNLOAD_PAGE_SIZE,
  ROW_STATUS,
  STEP_LABEL,
  TASK_OUTCOME,
} from './constants';

/** Led by the id column the upload's own file had: account_id or customer_id. */
const resultsCsvHeader = (idColumn) => [
  idColumn,
  'line_number',
  'status',
  'processed',
  'steps',
];

/**
 * Excel reads a bare UTF-8 CSV as the local codepage. fraud-rules-service strips
 * the mark on the way back in, so a downloaded file stays re-uploadable.
 */
const BYTE_ORDER_MARK = '\uFEFF';

/**
 * Excel evaluates a field starting with one of these on open, quoting or not. A
 * step detail is service passthrough and an account id came from an agent's own
 * file, so neither is ours to trust.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/** Always quoted: a step detail is free text and may hold a comma or a newline. */
const cell = (value) => {
  const text = String(value ?? '');
  const safe = FORMULA_LEAD.test(text) ? `'${text}` : text;

  return `"${safe.replaceAll('"', '""')}"`;
};

const label = (value, labels) => labels[value]?.label ?? value;

/** The Steps column of the table, flattened — outcome per step, and why. */
const steps = (tasks) =>
  tasks
    .map(({ taskId, outcome, detail }) => {
      const step = `${STEP_LABEL[taskId] ?? taskId}: ${label(outcome, TASK_OUTCOME)}`;

      return detail ? `${step} (${detail})` : step;
    })
    .join('; ');

const asCsv = (rows, idColumn) =>
  BYTE_ORDER_MARK +
  [
    resultsCsvHeader(idColumn).join(','),
    ...rows.map((row) =>
      [
        cell(row.sourceId),
        cell(row.lineNumber),
        cell(label(row.status, ROW_STATUS)),
        cell(row.processedTz ? formatDateTime(row.processedTz, true) : ''),
        cell(steps(row.tasks)),
      ].join(','),
    ),
  ].join('\r\n');

/** `incident.csv` filtered to failed rows becomes `incident-failed-results.csv`. */
const nameFor = (filename, status) =>
  [
    filename.replace(/\.csv$/i, ''),
    status ? label(status, ROW_STATUS).toLowerCase().replaceAll(' ', '-') : '',
    'results.csv',
  ]
    .filter(Boolean)
    .join('-');

const save = (text, filename) => {
  const href = URL.createObjectURL(
    new Blob([text], { type: 'text/csv;charset=utf-8' }),
  );
  const link = document.createElement('a');

  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

/** The whole filtered set, not the page on screen, paged at [DOWNLOAD_PAGE_SIZE]. */
const useDownloadBulkOperationResults = ({
  uploadToken,
  filename,
  status,
  idColumn,
}) => {
  const client = useApolloClient();
  const [state, setState] = useState({ loading: false });

  // A failure describes one row set. Dropped rather than hidden when the agent
  // picks another, so coming back to the first does not resurrect it.
  if (state.error && state.status !== status) {
    setState({ loading: state.loading });
  }

  const download = useCallback(async () => {
    setState({ loading: true });

    try {
      const rows = [];

      for (let page = 0; ; page += 1) {
        const { data } = await client.query({
          query: BULK_UPLOAD_ROWS,
          variables: {
            uploadToken,
            status,
            page,
            size: DOWNLOAD_PAGE_SIZE,
          },
          fetchPolicy: 'no-cache',
        });

        const { content, totalElements } = data.bulkUploadRows;
        rows.push(...content);

        if (!content.length || rows.length >= totalElements) {
          break;
        }
      }

      save(asCsv(rows, idColumn), nameFor(filename, status));
      setState({ loading: false });
    } catch (error) {
      setState({ loading: false, error, status });
    }
  }, [client, uploadToken, filename, status, idColumn]);

  return [download, { loading: state.loading, error: state.error }];
};

export default useDownloadBulkOperationResults;
